import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Button } from "../../../components/SystemComponent/Button";
import { Modal } from "../../../components/SystemComponent/Modal";
import { MdFlight, MdCategory, MdAdd, MdEdit, MdDelete } from "react-icons/md";
import { createApi } from "../../../../core/api";

type TabKey = "type" | "model";

const TABS = [
  {
    key: "type" as TabKey,
    title: "Aircraft Types",
    icon: MdCategory,
    subTitle: "All aircraft types",
  },
  {
    key: "model" as TabKey,
    title: "Aircraft Type Models",
    icon: MdFlight,
    subTitle: "All aircraft models",
  },
];

export const AircraftManagement = () => {
  const api = useMemo(() => createApi(), []);

  const [tab, setTab] = useState<TabKey>("type");
  const [data, setData] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const [name, setName] = useState("");
  const [aircraftTypeId, setAircraftTypeId] = useState<number | "">("");
  const [isJet, setIsJet] = useState(false);
  const [typeIsCargo, setTypeIsCargo] = useState(false);

  const loadTypes = useCallback(async () => {
    const r = await api.aircraftType.getAll();
    if (r?.ok) {
      const list = r.data?.data ?? r.data ?? [];
      setTypes(Array.isArray(list) ? list : []);
    }
  }, [api]);

  const load = useCallback(async () => {
    setLoading(true);
    if (tab === "type") {
      const r = await api.aircraftType.getAll();
      if (r?.ok) {
        const list = r.data?.data ?? r.data ?? [];
        setData(Array.isArray(list) ? list : []);
      }
    } else {
      await loadTypes();
      const r = await api.aircraftTypeModel.getAll();
      if (r?.ok) {
        const list = r.data?.data ?? r.data ?? [];
        setData(Array.isArray(list) ? list : []);
      }
    }
    setLoading(false);
  }, [tab, api, loadTypes]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setAircraftTypeId("");
    setIsJet(false);
    setTypeIsCargo(false);
    setOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setName(row.name ?? "");
    setAircraftTypeId(row.aircraftTypeId ?? "");
    setIsJet(!!row.isJet);
    setTypeIsCargo(!!row.typeIsCargo);
    setOpen(true);
  };

  const submit = async () => {
    if (!name.trim()) return;
    if (tab === "model" && !aircraftTypeId) return;

    if (tab === "type") {
      const payload = { name };
      if (editing) await api.aircraftType.update(editing.id, payload);
      else await api.aircraftType.create(payload);
    } else {
      const payload = {
        name,
        aircraftTypeId,
        isJet,
        typeIsCargo,
      };
      if (editing) await api.aircraftTypeModel.update(editing.id, payload);
      else await api.aircraftTypeModel.create(payload);
    }

    setOpen(false);
    load();
  };

  const doDelete = async (id: number) => {
    if (tab === "type") await api.aircraftType.delete(id);
    else await api.aircraftTypeModel.delete(id);
    load();
  };

  const columns = useMemo<any[]>(() => {
    if (tab === "type") {
      return [
        {
          key: "id",
          title: "ID",
          minWidth: 80,
          accessor: (r: any) => r.id,
        },
        {
          key: "name",
          title: "Name",
          minWidth: 260,
          render: (r: any) => (
            <Badge
              icon={MdCategory}
              label={r.name}
              color="#2563EB"
              bg="rgba(37,99,235,.15)"
            />
          ),
        },
        {
          key: "actions",
          title: "Actions",
          minWidth: 200,
          render: (row: any) => (
            <GroupButton
              items={[
                {
                  id: "edit",
                  icon: MdEdit,
                  color: "#2563EB",
                  onClick: () => openEdit(row),
                },
                {
                  id: "delete",
                  icon: MdDelete,
                  color: "#DC2626",
                  onClick: () => doDelete(row.id),
                },
              ]}
            />
          ),
        },
      ];
    }

    return [
      {
        key: "id",
        title: "ID",
        minWidth: 80,
        accessor: (r: any) => r.id,
      },
      {
        key: "type",
        title: "Aircraft Type",
        minWidth: 220,
        render: (r: any) => (
          <Badge
            icon={MdCategory}
            label={r.aircraftType?.name ?? "-"}
            color="#0F766E"
            bg="rgba(15,118,110,.15)"
          />
        ),
      },
      {
        key: "name",
        title: "Model Name",
        minWidth: 240,
        render: (r: any) => (
          <Badge
            icon={MdFlight}
            label={r.name}
            color="#7C3AED"
            bg="rgba(124,58,237,.15)"
          />
        ),
      },
      {
        key: "jet",
        title: "Jet",
        minWidth: 120,
        render: (r: any) => (
          <Badge
            label={r.isJet ? "Yes" : "No"}
            color={r.isJet ? "#16A34A" : "#DC2626"}
            bg={r.isJet ? "rgba(22,163,74,.15)" : "rgba(220,38,38,.15)"}
          />
        ),
      },
      {
        key: "cargo",
        title: "Cargo",
        minWidth: 120,
        render: (r: any) => (
          <Badge
            label={r.typeIsCargo ? "Yes" : "No"}
            color={r.typeIsCargo ? "#0284C7" : "#64748B"}
            bg={r.typeIsCargo ? "rgba(2,132,199,.15)" : "rgba(100,116,139,.15)"}
          />
        ),
      },
      {
        key: "actions",
        title: "Actions",
        minWidth: 200,
        render: (row: any) => (
          <GroupButton
            items={[
              {
                id: "edit",
                icon: MdEdit,
                color: "#2563EB",
                onClick: () => openEdit(row),
              },
              {
                id: "delete",
                icon: MdDelete,
                color: "#DC2626",
                onClick: () => doDelete(row.id),
              },
            ]}
          />
        ),
      },
    ];
  }, [tab]);

  const tabMeta = TABS.find((x) => x.key === tab)!;

  return (
    <div style={S.wrap}>
      <div style={S.tabs}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.key === tab;
          return (
            <div
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{ ...S.tab, ...(active ? S.tabActive : {}) }}
            >
              <Icon size={18} />
              {t.title}
            </div>
          );
        })}
      </div>

      <div style={S.header}>
        <div>
          <div style={S.title}>{tabMeta.title}</div>
          <div style={S.subTitle}>{tabMeta.subTitle}</div>
        </div>

        <Button variant="primary" icon={MdAdd} onClick={openCreate}>
          Create
        </Button>
      </div>

      <Table
        tableKey={`aircraft-${tab}`}
        loading={loading}
        columns={columns}
        data={data}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />

      <Modal open={open} onClose={() => setOpen(false)}>
        <div style={S.modalTitle}>{editing ? "Update" : "Create"}</div>

        <div style={S.form}>
          {tab === "model" && (
            <>
              <label style={S.label}>Aircraft Type</label>
              <select
                value={aircraftTypeId}
                onChange={(e) => setAircraftTypeId(Number(e.target.value))}
                style={S.input}
              >
                <option value="">Select</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </>
          )}

          <label style={S.label}>Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={S.input}
          />

          {tab === "model" && (
            <>
              <label style={S.checkbox}>
                <input
                  type="checkbox"
                  checked={isJet}
                  onChange={(e) => setIsJet(e.target.checked)}
                />
                Jet
              </label>
              <label style={S.checkbox}>
                <input
                  type="checkbox"
                  checked={typeIsCargo}
                  onChange={(e) => setTypeIsCargo(e.target.checked)}
                />
                Cargo
              </label>
            </>
          )}
        </div>

        <div style={S.actions}>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit}>
            Save
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: {
    position: "absolute",
    inset: 0,
    padding: 20,
    background: "#F8FAFC",
  },
  tabs: {
    display: "flex",
    gap: 10,
    marginBottom: 16,
  },
  tab: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 16px",
    borderRadius: 12,
    background: "#E2E8F0",
    cursor: "pointer",
    fontWeight: 600,
    color: "#334155",
  },
  tabActive: {
    background: "#2563EB",
    color: "#fff",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: "#0F172A",
  },
  subTitle: {
    fontSize: 13,
    color: "#64748B",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 14,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: "#334155",
  },
  checkbox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 600,
    color: "#334155",
  },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
};
